/**
 * lib/tilePyramid.js — pure math for the tile pyramid client.
 *
 * Pyramid meta shape (matches backend):
 *   {
 *     sheet_id, tile_size, full_width_pts, full_height_pts,
 *     levels: [{ z, width, height, cols, rows }, ...]
 *   }
 *
 * Conventions used by the renderer:
 *   - The "natural" image dims the canvas physics use = meta.levels[0].{width,height}.
 *     ALL tiles upscale to fit the same normalised image rectangle, so pins,
 *     measurements, markups, revision clouds — anything that anchors against
 *     image-local coords — stay spatially locked regardless of which level
 *     is currently visible.
 *   - Tile `{z, x, y}` covers image-local pixel rect:
 *       sx = x * tile_size * (L0.width / Lz.width)
 *       sy = y * tile_size * (L0.height / Lz.height)
 *       sw = tile_size * (L0.width / Lz.width)
 *       sh = tile_size * (L0.height / Lz.height)
 *     Where L0 = the base level. All higher levels render into the same
 *     visible rectangle — they just bring more pixels per inch.
 */

/**
 * Pick the level whose effective rendered density matches the current
 * camera scale. Slight bias toward "next sharper level" so the user sees
 * crisp pixels at the threshold rather than slightly fuzzy ones.
 */
export function idealLevel(viewport, containerDims, meta) {
  if (!meta || !meta.levels?.length) return 0;
  const base = meta.levels[0];
  // CSS-pixels-per-image-pixel actually painted to the screen.
  const pxPerImgPx = viewport.s; // canvas scales the L0 image by vp.s
  // For each level, screen pixels per source pixel of that level:
  //   src→css = pxPerImgPx * (base.width / level.width)
  // Levels are ordered LOWEST-resolution first (level[0].width is the
  // smallest), so this ratio SHRINKS monotonically as i increases. Pick
  // the SMALLEST i (least resolution, fewest tiles) whose ratio already
  // satisfies <= 1.05 — i.e. stop climbing to sharper levels the moment
  // one is "good enough". At extreme zoom-out every level satisfies the
  // threshold; without stopping early the old loop kept overwriting
  // `chosen` all the way to the sharpest (highest-tile-count) level,
  // which starved the LRU_CAP tile cache and left most of the viewport
  // unpainted (bug found via tablet-viewport/field-mode zoom testing —
  // reproduced a checkerboard/degrading render at 8% fit-zoom).
  for (let i = 0; i < meta.levels.length; i++) {
    const ratio = pxPerImgPx * (base.width / meta.levels[i].width);
    if (ratio <= 1.05) return i;
  }
  return meta.levels.length - 1;
}

/**
 * Compute the set of {z, x, y} tile coords intersecting the visible
 * viewport at level z. Returns an array sorted by distance from viewport
 * centre (so viewport-priority loading lands the centre tile first).
 */
export function tilesForViewport(viewport, containerDims, meta, z) {
  if (!meta || !meta.levels?.[z]) return [];
  const base = meta.levels[0];
  const level = meta.levels[z];
  // Image-local rect visible on screen. vp.s scales L0 (the natural-dims
  // image) and (vp.tx, vp.ty) translates it.
  const viewLeft   = -viewport.tx / viewport.s;
  const viewTop    = -viewport.ty / viewport.s;
  const viewRight  = viewLeft + containerDims.w / viewport.s;
  const viewBottom = viewTop  + containerDims.h / viewport.s;

  // Tile size in L0-image-coords (this is what the renderer uses to place
  // tiles inside the natural-dims rectangle).
  const tilePxInL0 = (level.width > 0)
    ? (base.width / level.width) * meta.tile_size
    : meta.tile_size;
  if (tilePxInL0 <= 0) return [];

  const x0 = Math.max(0, Math.floor(viewLeft   / tilePxInL0));
  const y0 = Math.max(0, Math.floor(viewTop    / tilePxInL0));
  const x1 = Math.min(level.cols - 1, Math.floor(viewRight  / tilePxInL0));
  const y1 = Math.min(level.rows - 1, Math.floor(viewBottom / tilePxInL0));

  const cx = (viewLeft + viewRight) / 2 / tilePxInL0;
  const cy = (viewTop + viewBottom) / 2 / tilePxInL0;

  const out = [];
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const dx = x + 0.5 - cx;
      const dy = y + 0.5 - cy;
      out.push({ z, x, y, _d2: dx * dx + dy * dy });
    }
  }
  out.sort((a, b) => a._d2 - b._d2);
  return out;
}

/**
 * Image-local (L0-coordinate) rect a given tile covers. Used to position
 * the <img> inside the natural-dims wrapper. Returns {left, top, width,
 * height} in L0 pixels.
 */
export function tileRectInBase(tile, meta) {
  const base = meta.levels[0];
  const level = meta.levels[tile.z];
  const tilePxInL0 = (base.width / level.width) * meta.tile_size;
  return {
    left: tile.x * tilePxInL0,
    top:  tile.y * tilePxInL0,
    width:  tilePxInL0,
    height: tilePxInL0,
  };
}

export function tileKey(t) { return `${t.z}/${t.x}_${t.y}`; }
